import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { User } from "src/app/models/User.model";
import { k } from "src/environments/properties";
import { MyUtils } from "../support/utils";
import { HttpService } from "./HttpService.service";
import { Router } from "@angular/router";
import { NotificationService } from "./notification.service";
import { jwtDecode } from 'jwt-decode';

export interface GenerateMFAResponse {
  qrCodeUrl: string;
  secret: string;
}

export interface ValidateMFAResponse {
  // ajusta estas propiedades según lo que realmente responda tu backend
  message?: string;
  success?: boolean;
}

export interface LoginResponse {
  requires_2fa: boolean;

  partial_token?: string;

  access_token?: string;

  user?: User;
}

@Injectable()
export class AuthService {
  private endpoint = 'auth';

  currentUser = new BehaviorSubject({} as User);
  // 👈 NUEVO: El token ahora es reactivo
  public token$ = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpService,
    private router: Router,
    private notification: NotificationService,
  ) {
    const rawUser = localStorage.getItem(k.userInformation);
    if (rawUser) {
      try {
        const u = JSON.parse(rawUser);
        this.currentUser.next(u);
      } catch (e) {

        this.currentUser.next({} as User);
      }
    } else {
      this.currentUser.next({} as User);
    }
  }

  // 👇 helper cómodo para obtener el usuario actual
  get currentUserValue(): User | null {
    return this.currentUser.value;
  }

  // 🔑 Login: usa RT en cookie httpOnly + access_token en body
  async login(
    username: string,
    password: string
  ): Promise<LoginResponse> {

    MyUtils.clearEnvironment();

    const res = await this.http.post<LoginResponse>(
      `${this.endpoint}/login`,
      {
        username,
        password
      },
      null,
      true
    );

    /*
     * Login pendiente de MFA.
     */
    if (
      res.requires_2fa &&
      res.partial_token
    ) {

      localStorage.setItem(
        k.partial_token,
        res.partial_token
      );

      this.token$.next(null);

      return res;
    }

    /*
     * Login completamente autenticado.
     */
    if (
      !res.requires_2fa &&
      res.access_token
    ) {

      localStorage.setItem(
        k.token,
        res.access_token
      );

      this.token$.next(
        res.access_token
      );

      if (res.user) {

        this.currentUser.next(
          res.user
        );

        localStorage.setItem(
          k.userInformation,
          JSON.stringify(res.user)
        );
      }
    }

    return res;
  }

  // 🔁 Refresh: backend devuelve { access_token }, RT via cookie
  async refresh(): Promise<string> {
    const res = await this.http.post<{ access_token: string }>(
      `${this.endpoint}/refresh`,
      {},
      null,
      true, // withCredentials → envía cookie rt
    );

    localStorage.setItem(k.token, res.access_token);
    document.cookie = "access_token=" + res.access_token + "; path=/; secure; samesite=strict;";
    this.token$.next(res.access_token);
    return res.access_token;
  }

  async logoutRemote(): Promise<void> {
    try {
      await this.http.post('auth/logout', {}, null, true);
    } catch (e) {
      console.warn('Error en logout remoto, continuo con logout local', e);
    }
  }

  /**
   * Limpia estado local. No llama al backend.
   * reason:
   *  - 'manual'  -> usuario hizo logout
   *  - 'expired' -> sesión expirada / refresh falló
   */
  forceLocalLogout(
  redirect = true,
  reason: 'manual' | 'expired' | 'mfa-reset' = 'manual'
) {
  MyUtils.clearEnvironment();

  localStorage.removeItem(k.partial_token);

  this.currentUser.next({} as User);
  this.token$.next(null);

  if (reason === 'expired') {
    this.notification.showWarning(
      'Tu sesión ha expirado. Por favor inicia sesión nuevamente.',
      'Sesión expirada'
    );

  } else if (reason === 'mfa-reset') {
    this.notification.showWarning(
      'Por seguridad, debes iniciar sesión nuevamente para reconfigurar tu autenticación de doble factor.',
      'Reconfiguración de seguridad'
    );

  } else {
    this.notification.showSuccess(
      'Has cerrado sesión correctamente.',
      'Sesión cerrada'
    );
  }

  if (redirect) {
    this.router.navigate(['/auth/login']);
  }
}

  async logout(redirect = true): Promise<void> {
    await this.logoutRemote();
    this.forceLocalLogout(redirect, 'manual');
  }


  getToken(
    type: 'access' | 'partial' = 'access'
  ): string | null {

    if (type === 'partial') {

      return localStorage.getItem(
        k.partial_token
      );

    }

    return (
      sessionStorage.getItem(k.token) ||
      localStorage.getItem(k.token)
    );
  }

  isAuthenticate(): boolean {
    return !!this.getToken();
  }

  async dataUser(token: string) {
    return this.http.get<any>(
      'usuarios/profile',
      null,
      { Authorization: `Bearer ${token}` },
      true,
    );
  }

  hasRole(role: string): boolean {
    const user = this.currentUserValue;
    return !!user?.roles?.some(
      userRole => userRole.nombre === role
    );
  }

  hasAnyRole(roles: string[]): boolean {
    const user = this.currentUserValue;

    if (!user?.roles?.length) {
      return false;
    }

    return roles.some(role =>
      user.roles.some(userRole => userRole.nombre === role)
    );
  }

  getTokenAuth(): string | null {
    return localStorage.getItem('web_token'); // Solo leemos el token crudo
  }

  getRoles(): string[] {
    const token = this.token$.value || this.getTokenAuth();
    if (!token) return [];

    try {
      // Decodificamos el payload del token
      const decoded: any = jwtDecode(token);
      // Asumiendo que el JWT tiene un campo 'roles' o 'role'      
      return decoded.roles || [];
    } catch (error) {
      return [];
    }
  }

  hasRoleAuth(role: string): boolean {
    return this.getRoles().includes(role);
  }

  canManageItem(item: any, accesUser: boolean): boolean {
    return (!item.publica || accesUser);
  }


  async generateMFACode(): Promise<GenerateMFAResponse> {
    return await this.http.post(
      `${this.endpoint}/2fa/qrcode/generate`,
      {},
      null,
      true
    ) as GenerateMFAResponse;
  }

  async validateMFACode(code: string): Promise<ValidateMFAResponse> {
    const res = await this.http.post(
      `${this.endpoint}/2fa/validate`,
      {
        code
      },
      null,
      true
    );

    return res as ValidateMFAResponse;
  }

  async loginMFACode(code: string) {
    return await this.http.post(
      `${this.endpoint}/2fa/login`,
      {
        code
      },
      null,
      true
    );
  }


}
